import { motion } from "framer-motion";
import { Cpu, Boxes, Workflow, Layers, Zap } from "lucide-react";

const CAPABILITIES = [
    { icon: Cpu, title: "GPU Marketplace", body: "Open marketplace of GPU nodes — A100/H100-class, scheduled on-demand." },
    { icon: Boxes, title: "AI Model Registry", body: "Publish, version, and meter open and proprietary models with on-chain provenance." },
    { icon: Workflow, title: "Inference Routing", body: "AI-powered routing places workloads on the lowest-latency, lowest-cost capable node." },
    { icon: Layers, title: "Decentralized Training", body: "Distributed fine-tuning across the network with deterministic checkpointing." },
];

const MARKETPLACE = [
    { name: "llama-3.1-70b", gpu: "H100", region: "US-EAST", latency: "84ms", load: "78%", rate: "0.0048", status: "Hot" },
    { name: "mixtral-8x22b", gpu: "H100", region: "EU-WEST", latency: "62ms", load: "62%", rate: "0.0061", status: "Live" },
    { name: "sdxl-turbo", gpu: "A100", region: "AP-SOUTH", latency: "118ms", load: "44%", rate: "0.0019", status: "Live" },
    { name: "whisper-v3", gpu: "A100", region: "US-WEST", latency: "55ms", load: "31%", rate: "0.0012", status: "Live" },
];

export default function AIInfra() {
    return (
        <section id="ai" className="relative py-24 md:py-32 lg:py-40 border-t border-line overflow-hidden" data-testid="ai-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl mb-16">
                    <div className="overline mb-4">05 — Decentralized AI Compute</div>
                    <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.06] tracking-tight text-ink">
                        An Open GPU Network<br />
                        <span className="brand-gradient-text">for the AI Economy.</span>
                    </h2>
                    <p className="mt-6 text-base text-muted-ink max-w-2xl leading-relaxed">
                        AI compute is becoming one of the most valuable infrastructure markets in the world. DeCloudX creates a decentralized GPU layer where developers can deploy models, run inference, fine-tune workloads, and access distributed compute without depending only on centralized hyperscalers.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
                    {CAPABILITIES.map((c, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            className="surface card-lift p-6 rounded-2xl"
                            data-testid={`ai-cap-${i}`}
                        >
                            <div className="w-10 h-10 rounded-xl bg-card-muted border border-line flex items-center justify-center mb-4">
                                <c.icon size={18} className="text-brand-violet" strokeWidth={1.5} />
                            </div>
                            <div className="font-heading font-bold text-base text-ink">{c.title}</div>
                            <p className="text-sm text-muted-ink mt-2 leading-relaxed">{c.body}</p>
                        </motion.div>
                    ))}
                </div>

                <div className="surface-strong rounded-2xl overflow-hidden" data-testid="ai-marketplace">
                    <div className="flex items-center justify-between px-6 md:px-8 py-4 border-b border-line bg-card-muted">
                        <div className="flex items-center gap-3">
                            <Zap size={14} className="text-brand-violet" strokeWidth={1.5} />
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-ink">GPU & Model Marketplace · Live</span>
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">Settled in $DCX</span>
                    </div>
                    <div>
                        <div className="hidden md:grid grid-cols-12 gap-3 px-6 md:px-8 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-faint-ink border-b border-line bg-card-muted">
                            <div className="col-span-3">Model</div>
                            <div className="col-span-1">GPU</div>
                            <div className="col-span-2">Region</div>
                            <div className="col-span-1 text-right">Latency</div>
                            <div className="col-span-2 text-right">Load</div>
                            <div className="col-span-1 text-right">$DCX</div>
                            <div className="col-span-2 text-right">Status</div>
                        </div>
                        {MARKETPLACE.map((m, i) => (
                            <div key={i} className="grid grid-cols-2 md:grid-cols-12 gap-3 px-6 md:px-8 py-4 hover:bg-card-muted transition-colors items-center border-b border-line last:border-b-0" data-testid={`marketplace-row-${i}`}>
                                <div className="md:col-span-3 font-mono text-sm text-ink">{m.name}</div>
                                <div className="md:col-span-1 font-mono text-xs text-brand-violet">{m.gpu}</div>
                                <div className="md:col-span-2 font-mono text-xs text-muted-ink">{m.region}</div>
                                <div className="md:col-span-1 md:text-right font-mono text-xs text-muted-ink">{m.latency}</div>
                                <div className="md:col-span-2 md:text-right">
                                    <div className="hidden md:block h-1 w-full max-w-[120px] ml-auto rounded-full bg-card-muted overflow-hidden">
                                        <div className="h-full brand-gradient-bg" style={{ width: m.load }} />
                                    </div>
                                    <div className="font-mono text-[10px] text-muted-ink mt-1 md:text-right">{m.load}</div>
                                </div>
                                <div className="md:col-span-1 md:text-right font-mono text-xs text-brand-blue">{m.rate}</div>
                                <div className="md:col-span-2 md:text-right">
                                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-wider ${
                                        m.status === "Hot"
                                            ? "bg-brand-violet/10 border border-brand-violet/30 text-brand-violet"
                                            : "bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                                    }`}>
                                        <span className="w-1 h-1 rounded-full bg-current" />
                                        {m.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
